// src/services/businessIntelligenceService.ts
// Business Intelligence Service for Defense Planning App - WITH FORMULA EXPLAINERS
import apiConfig from '../config/apiConfig';
import { authHeaders } from './authHeaders';

// ✅ NEW API Response interface (from your actual API)
interface BusinessIntelligenceResponse {
  success: boolean;
  data: BusinessIntelligenceApiData;
  error?: string; // ✅ Added for backward compatibility with calling code
}

interface BusinessIntelligenceApiData {
  assessmentId: number;
  companyId: number;
  companyName: string;
  industry: string;
  
  // Revenue Information - THE KEY FIELDS WE NEED
  annualRevenue: number;           // e.g., 3000000000 ($3B)
  dailyRevenue: number;            // e.g., 8219178
  
  // Risk Information  
  revenueAtRisk: string;          // Formatted string like "$32.9M"
  revenueAtRiskRaw: number;       // Raw number like 32876712
  riskScore: number;
  recoveryDays: number;
  recoveryLevel: string;
  
  // Metadata
  isActualRevenue: boolean;
  calculationSource: string;
  calculatedAt: string;
  assessmentDate: string;
  environment: string;
  version: string;
  
  // Methodology Details
  methodology: {
    formula: string;
    dataSource: string;
    dailyRevenueFormula: string;
    recoveryDaysFormula: string;
  };
}

// ✅ LEGACY interface (for backward compatibility with existing code)
interface BusinessIntelligenceData {
  assessmentId: string;
  error?: string;
  // null when revenue-at-risk was never measured for this session — never a
  // zeroed/estimated object standing in for a real one.
  revenueAtRisk: {
    amount: number;
    formatted: string;
    calculation: {
      actualRevenue: number | null;
      dailyRevenue: number | null;
      averageRiskScore: number;
      recoveryDays: number;
      explanation?: string;
    };
  } | null;
  // null when annual revenue was never measured for this session.
  actualRevenue: {
    amount: number;
    formatted: string;
  } | null;
  recoveryTimeline?: {
    days: number;
    formatted: string;
    level: string;
  };
  companyProfile?: {
    name: string;
    industry: string;
    revenue: number | null;
    employeeCount: number | null;
  };
}

interface CategoryMultiplier {
  underOptimal: number;
  overOptimal: number;
}

interface FrictionMultipliers {
  governance: CategoryMultiplier;
  culture: CategoryMultiplier;
  budget: CategoryMultiplier;
  technical_debt: CategoryMultiplier;
  third_party: CategoryMultiplier;
  people: CategoryMultiplier;
}

interface FrictionCalculationResult {
  // null for all three when revenue was never measured — an explicit marker,
  // never a scaling factor/multiplier set computed from a null dividend.
  scalingFactor: number | null;
  annualRevenue: number | null;
  multipliers: FrictionMultipliers | null;
  // True exactly when the fields above are null because revenue is
  // unmeasured (not because it is a genuine, measured 0 — a $0-revenue
  // company DOES get a real scalingFactor of 0 and revenueUnmeasured:false).
  revenueUnmeasured: boolean;
  // NEW: Formula explanation data
  explanation: {
    baselineRevenue: number;
    scalingMethod: string;
    scalingRationale: string;
    exampleCalculations: {
      [key: string]: {
        baseAmount: number;
        scaledAmount: number;
        explanation: string;
      };
    };
  };
}

// NEW: Formula explainer interface
interface FormulaExplanation {
  title: string;
  description: string;
  methodology: string;
  dataSource: string;
  examples: {
    [key: string]: string;
  };
  assumptions: string[];
}

type CurrencyScale = 'M' | 'B' | 'auto';

// Simple API URL detection
const getApiBaseUrl = (): string => {
  // Simple check: if we're on the production domain, use relative URLs
  if (window.location.hostname.includes('insights-risk-aperture.com')) {
    return ''; // Use relative URLs, nginx will proxy /api/* to backend
  }
  
  // Development fallback
  return apiConfig.getBaseUrl();
};

const API_BASE_URL = getApiBaseUrl();
console.log('🌐 Defense Planning API Base URL:', API_BASE_URL);

export const defensePlanningBusinessService = {
  /**
   * The ONE place the client decides what an absent revenue means: nothing.
   * ⛔ Never `|| <number>` here — that is how five different fallbacks
   * ($250M, $100M, $10M, 1, 0) came to coexist for one missing value.
   * A genuine 0 (a company that reported zero revenue) is a measurement
   * and must survive as 0, distinct from null.
   */
  normalizeRevenue(businessData: { annualRevenue?: number | null } | null | undefined): number | null {
    const v = businessData?.annualRevenue;
    return v === null || v === undefined ? null : v;
  },

  /**
   * Smart revenue extraction - handles all interface formats for backward compatibility
   */
  extractAnnualRevenue(businessData: BusinessIntelligenceResponse | BusinessIntelligenceApiData | BusinessIntelligenceData): number | null {
    // Format 1: BusinessIntelligenceResponse (new API with success/data wrapper)
    if ('success' in businessData && 'data' in businessData) {
      return this.normalizeRevenue(businessData.data);
    }

    // Format 2: BusinessIntelligenceApiData (new API data directly)
    if ('annualRevenue' in businessData && typeof businessData.annualRevenue === 'number') {
      return businessData.annualRevenue;
    }

    // Format 3: BusinessIntelligenceData (legacy format)
    if ('actualRevenue' in businessData && businessData.actualRevenue?.amount !== null && businessData.actualRevenue?.amount !== undefined) {
      return businessData.actualRevenue.amount;
    }

    // Fallback: try to find revenue in legacy nested structure (with proper type checking)
    if ('revenueAtRisk' in businessData &&
        typeof businessData.revenueAtRisk === 'object' &&
        businessData.revenueAtRisk !== null &&
        'calculation' in businessData.revenueAtRisk &&
        businessData.revenueAtRisk.calculation?.actualRevenue !== null &&
        businessData.revenueAtRisk.calculation?.actualRevenue !== undefined) {
      return businessData.revenueAtRisk.calculation.actualRevenue;
    }

    // Genuinely unmeasured — no numeric stand-in.
    console.warn('⚠️ Could not extract annual revenue from business data; reporting as unmeasured (null)');
    return null;
  },

  /**
   * Get authentication token from URL or localStorage
   */
  getAuthToken(): string | null {
    // Try to get token from URL first (passed from Insights)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    
    if (urlToken) {
      localStorage.setItem('defenseAuthToken', urlToken);
      return urlToken;
    }
    
    // Fall back to stored token
    return localStorage.getItem('defenseAuthToken') || 
           localStorage.getItem('authToken') || 
           localStorage.getItem('token');
  },


  /**
   * Convert raw BI API response to BusinessIntelligenceData format.
   * Used both by getBusinessIntelligence and by callers that already have the raw response.
   */
  convertRawToBusinessData(data: any, assessmentId: string): BusinessIntelligenceData {
    // One decision for the whole conversion: a measured actualRevenue.amount
    // wins over annualRevenue, but neither is coalesced to a numeric guess.
    const revenue = data.actualRevenue?.amount !== null && data.actualRevenue?.amount !== undefined
      ? data.actualRevenue.amount
      : this.normalizeRevenue(data);

    // revenueAtRisk is its own measurement — never estimated as a % of revenue.
    const revenueAtRiskAmount = data.revenueAtRiskRaw !== null && data.revenueAtRiskRaw !== undefined
      ? data.revenueAtRiskRaw
      : (data.revenueAtRisk?.amount !== null && data.revenueAtRisk?.amount !== undefined
          ? data.revenueAtRisk.amount
          : null);

    return {
      assessmentId: assessmentId,
      error: undefined,
      // A zeroed/estimated object reads as measured data — emit null for the
      // whole thing instead when revenue was never measured.
      actualRevenue: revenue === null
        ? null
        : {
            amount: revenue,
            formatted: data.actualRevenue?.formatted || this.formatCurrency(revenue, 'auto')
          },
      revenueAtRisk: revenueAtRiskAmount === null
        ? null
        : {
            amount: revenueAtRiskAmount,
            formatted: typeof data.revenueAtRisk === 'string' ? data.revenueAtRisk :
                       (data.revenueAtRisk?.formatted || this.formatCurrency(revenueAtRiskAmount, 'auto')),
            calculation: {
              actualRevenue: revenue,
              dailyRevenue: data.dailyRevenue !== null && data.dailyRevenue !== undefined
                ? data.dailyRevenue
                : (revenue === null ? null : revenue / 365),
              averageRiskScore: data.risk_score || data.riskScore || 59,
              recoveryDays: data.recoveryTimeline?.days || data.recoveryDays || 20,
              explanation: 'Calculated from Business Intelligence API'
            }
          },
      recoveryTimeline: {
        days: data.recoveryTimeline?.days || data.recoveryDays || 11,
        formatted: data.recoveryTimeline?.formatted || `${data.recoveryTimeline?.days || data.recoveryDays || 11} days`,
        level: data.recoveryTimeline?.level || data.recoveryLevel || 'Standard'
      },
      companyProfile: data.companyProfile || {
        name: data.companyName || 'Unknown Company',
        industry: data.industry || 'General',
        revenue: revenue,
        // Unreachable today (the route always sends companyProfile), but a bare fabricated
        // 500 is still wrong if that ever changes. Carry the unmeasured state honestly.
        employeeCount: (data.totalEmployees === null || data.totalEmployees === undefined)
          ? null
          : data.totalEmployees
      }
    };
  },

  /**
   * Fetch business intelligence data for friction cost calculations
   * CLEAN VERSION - Direct database API call only
   */
  async getBusinessIntelligence(assessmentId: string): Promise<BusinessIntelligenceData> {
    try {
      // The /api/business-intelligence router is gated (acceptCoordinatorOrIntegration,
      // v3.65) — an anonymous request 401s. See services/authHeaders.ts.
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...authHeaders(),
      };

      const apiUrl = `${API_BASE_URL}/api/business-intelligence/${assessmentId}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Authentication failed. Please navigate from the Insights dashboard with a valid session.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json() as any;
      return this.convertRawToBusinessData(data, assessmentId);
      
    } catch (error) {
      console.error('❌ Defense Planning: Error fetching business intelligence:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // The request failed — we have no measurement at all. Emit null for
      // both revenue objects rather than a fabricated dataset; the consumer
      // suppresses on null instead of rendering invented figures as real.
      console.log('🔄 Defense Planning: Business intelligence unavailable; reporting as unmeasured');
      return {
        assessmentId: assessmentId,
        error: `Could not load business intelligence: ${errorMessage}`,
        actualRevenue: null,
        revenueAtRisk: null,
        recoveryTimeline: {
          days: 22,
          formatted: '22 days',
          level: 'Standard'
        }
      };
    }
  },

  /**
   * Calculate dynamic friction cost multipliers based on company revenue
   * ✅ ENHANCED with formula explanations and square root scaling rationale
   * ✅ BACKWARD COMPATIBLE with legacy interface and new API response formats
   */
  calculateFrictionMultipliers(businessData: BusinessIntelligenceResponse | BusinessIntelligenceApiData | BusinessIntelligenceData): FrictionCalculationResult {
    // ✅ Smart revenue extraction - handles ALL interface formats
    const annualRevenue = this.extractAnnualRevenue(businessData);

    // Base multipliers for a $100M company (our baseline)
    const baselineRevenue = 100000000; // $100M baseline company

    // ⛔ Never let a null revenue reach `annualRevenue / baselineRevenue` — JS
    // coerces null to 0, so Math.sqrt(0) silently zeroes every multiplier and
    // a board reader sees a confident "$0/year friction" instead of "we don't
    // know". Explicit null check; return an unmeasured marker instead.
    if (annualRevenue === null) {
      console.warn('⚠️ Defense Planning: Revenue unmeasured — friction multipliers cannot be scaled, returning unmeasured marker');
      return {
        scalingFactor: null,
        annualRevenue: null,
        multipliers: null,
        revenueUnmeasured: true,
        explanation: {
          baselineRevenue,
          scalingMethod: 'Square Root Scaling',
          scalingRationale: 'Revenue for this session has not been measured, so friction costs cannot be scaled against it.',
          exampleCalculations: {}
        }
      };
    }

    console.log(`💰 Defense Planning: Calculating friction multipliers for ${this.formatCurrency(annualRevenue, 'auto')} revenue`);

    // These represent ANNUAL operational inefficiency costs when categories are outside optimal zones
    // Based on 10 years of ExcelIntegratedRiskEngine data and industry benchmarks
    const baseMultipliers: FrictionMultipliers = {
      governance: { 
        underOptimal: 5000,   // $5K/year lost to insufficient governance (chaos, compliance gaps)
        overOptimal: 3000     // $3K/year lost to excessive governance (bureaucracy, over-control)
      },
      culture: { 
        underOptimal: 8000,   // $8K/year lost to poor security culture (risky behaviors, resistance)
        overOptimal: 4000     // $4K/year lost to over-rigid culture (innovation stifling, fear)
      },
      budget: { 
        underOptimal: 12000,  // $12K/year lost to under-investment (gaps, makeshift solutions)
        overOptimal: 6000     // $6K/year lost to over-investment (gold-plating, waste)
      },
      technical_debt: { 
        underOptimal: 8000,   // $8K/year lost to accumulated technical debt (maintenance, outages)
        overOptimal: 3000     // $3K/year lost to over-engineering (complexity, over-optimization)
      },
      third_party: { 
        underOptimal: 6000,   // $6K/year lost to inadequate vendor management (gaps, breaches)
        overOptimal: 4000     // $4K/year lost to excessive vendor oversight (friction, delays)
      },
      people: { 
        underOptimal: 10000,  // $10K/year lost to understaffing (burnout, gaps, turnover)
        overOptimal: 2000     // $2K/year lost to overstaffing (inefficiency, coordination overhead)
      }
    };

    // Calculate revenue scaling factor using SQUARE ROOT for diminishing returns
    // Rationale: Larger companies have economies of scale, better processes, and more resources
    // A $3B company doesn't have 30x the friction of a $100M company - more like 5.5x
    const scalingFactor = Math.sqrt(annualRevenue / baselineRevenue);
    
    // Create example calculations for explainer
    const exampleCalculations = {
      governance_under: {
        baseAmount: baseMultipliers.governance.underOptimal,
        scaledAmount: Math.round(baseMultipliers.governance.underOptimal * scalingFactor),
        explanation: `Insufficient governance friction scales with company complexity and regulatory scope`
      },
      budget_under: {
        baseAmount: baseMultipliers.budget.underOptimal,
        scaledAmount: Math.round(baseMultipliers.budget.underOptimal * scalingFactor),
        explanation: `Under-investment friction grows with revenue at risk and operational scale`
      }
    };
    
    // Apply scaling to all multipliers
    const scaledMultipliers: FrictionMultipliers = {
      governance: {
        underOptimal: Math.round(baseMultipliers.governance.underOptimal * scalingFactor),
        overOptimal: Math.round(baseMultipliers.governance.overOptimal * scalingFactor)
      },
      culture: {
        underOptimal: Math.round(baseMultipliers.culture.underOptimal * scalingFactor),
        overOptimal: Math.round(baseMultipliers.culture.overOptimal * scalingFactor)
      },
      budget: {
        underOptimal: Math.round(baseMultipliers.budget.underOptimal * scalingFactor),
        overOptimal: Math.round(baseMultipliers.budget.overOptimal * scalingFactor)
      },
      technical_debt: {
        underOptimal: Math.round(baseMultipliers.technical_debt.underOptimal * scalingFactor),
        overOptimal: Math.round(baseMultipliers.technical_debt.overOptimal * scalingFactor)
      },
      third_party: {
        underOptimal: Math.round(baseMultipliers.third_party.underOptimal * scalingFactor),
        overOptimal: Math.round(baseMultipliers.third_party.overOptimal * scalingFactor)
      },
      people: {
        underOptimal: Math.round(baseMultipliers.people.underOptimal * scalingFactor),
        overOptimal: Math.round(baseMultipliers.people.overOptimal * scalingFactor)
      }
    };

    console.log(`📊 Defense Planning: Revenue scaling factor: ${scalingFactor.toFixed(2)}x`);
    console.log(`🎯 Defense Planning: Scaled multipliers:`, scaledMultipliers);

    return {
      scalingFactor,
      annualRevenue,
      multipliers: scaledMultipliers,
      revenueUnmeasured: false,
      explanation: {
        baselineRevenue,
        scalingMethod: 'Square Root Scaling',
        scalingRationale: 'Larger companies benefit from economies of scale, better processes, and more resources. Friction costs grow slower than revenue due to operational efficiencies.',
        exampleCalculations
      }
    };
  },

  /**
   * NEW: Get detailed formula explanations for UI explainer buttons
   */
  getFormulaExplanations(): { [key: string]: FormulaExplanation } {
    return {
      frictionMultipliers: {
        title: 'Business Friction Cost Calculation',
        description: 'Annual operational inefficiency costs when security readiness categories fall outside optimal "Goldilocks" zones.',
        methodology: 'Square root scaling applied to baseline friction costs based on company revenue size. Formula: sqrt(companyRevenue / $100M baseline) × baseFrictionCost',
        dataSource: 'Based on 10 years of ExcelIntegratedRiskEngine data, industry benchmarks, and readiness zone analysis from thousands of assessments.',
        examples: {
          '$100M Company': 'Governance friction: $5K under-optimal, $3K over-optimal (1.0x scaling)',
          '$1B Company': 'Governance friction: $16K under-optimal, $9K over-optimal (3.16x scaling)',
          '$3B Company': 'Governance friction: $27K under-optimal, $16K over-optimal (5.48x scaling)'
        },
        assumptions: [
          'Friction costs represent annual operational inefficiency',
          'Both under-investment and over-investment create friction',
          'Larger companies have economies of scale (square root scaling)',
          'Optimal zones are industry-specific based on benchmarks',
          'Costs are additive across all misaligned categories'
        ]
      },
      goldilocksZones: {
        title: 'Goldilocks Readiness Zones',
        description: 'Optimal readiness ranges where organizations achieve maximum security efficiency - not too little, not too much.',
        methodology: 'Zones derived from ExcelIntegratedRiskEngine analysis of risk factors (F2, F3, F25, K7, etc.) using SUMIF logic and industry benchmarks.',
        dataSource: '10 years of assessment data, Excel Risk Calculator integration, industry-specific benchmarks for governance, technical debt, culture, third-party, budget, and people categories.',
        examples: {
          'Governance': 'Optimal 70-85% - Below 70%: compliance gaps, chaos. Above 85%: bureaucracy, over-control.',
          'Culture': 'Optimal 75-90% - Below 75%: risky behaviors, resistance. Above 90%: fear-based, innovation-stifling.',
          'Budget': 'Optimal 65-80% - Below 65%: under-investment, gaps. Above 80%: gold-plating, waste.'
        },
        assumptions: [
          'Optimal zones vary by industry and company size',
          'Zones based on statistical analysis of best-performing organizations',
          'Both under-optimization and over-optimization create business friction',
          'Readiness efficiency is maximized within optimal ranges',
          'Zones are continuously updated based on new assessment data'
        ]
      },
      revenueScaling: {
        title: 'Revenue-Based Friction Scaling',
        description: 'How business friction costs scale with company size using square root methodology.',
        methodology: 'Square root scaling: sqrt(actualRevenue / $100M baseline). Accounts for economies of scale while recognizing increased complexity.',
        dataSource: 'Analysis of friction cost patterns across company sizes from $10M to $10B+ revenue organizations.',
        examples: {
          '$10M Company': '0.32x scaling - Small companies have proportionally lower but still significant friction',
          '$250M Company': '1.58x scaling - Mid-market companies with growing complexity',
          '$10B Company': '10x scaling - Large enterprises with massive scale but better processes'
        },
        assumptions: [
          'Square root scaling reflects diminishing returns to scale',
          'Larger companies have better processes and resources',
          'Complexity grows slower than revenue due to operational efficiencies',
          'Baseline of $100M represents typical mid-market organization',
          'Industry variations are captured in readiness zone definitions'
        ]
      }
    };
  },

  /**
   * Format currency values for display
   */
  formatCurrency(amount: number | null | undefined, scale: CurrencyScale = 'auto'): string {
    // A null/undefined amount is unmeasured, not a genuine zero — '$0.0M' would
    // read as a confident figure. Callers that need a suppression element instead
    // of a string should check for null before calling this.
    if (amount === null || amount === undefined || Number.isNaN(amount)) return 'Not measured';
    if (amount === 0) return '$0.0M';
    
    if (scale === 'auto') {
      if (amount >= 1000000000) {
        return `$${(amount / 1000000000).toFixed(1)}B`;
      } else if (amount >= 1000000) {
        return `$${(amount / 1000000).toFixed(1)}M`;
      } else if (amount >= 1000) {
        return `$${(amount / 1000).toFixed(0)}K`;
      } else {
        return `$${amount.toLocaleString()}`;
      }
    }
    
    const divisor = scale === 'B' ? 1000000000 : 1000000;
    const formatted = (amount / divisor).toFixed(1);
    
    return `$${formatted}${scale}`;
  },

  /**
   * Get assessment ID from URL parameters
   */
  getAssessmentId(): string {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('assessmentId') || urlParams.get('id') || '7'; // Default to 7 (CAC22_Customer)
  },

  /**
   * NEW: Calculate what-if scenarios for friction optimization
   */
  calculateOptimizationImpact(currentReadiness: { [key: string]: number }, targetReadiness: { [key: string]: number }, businessData: BusinessIntelligenceResponse | BusinessIntelligenceApiData | BusinessIntelligenceData): {
    currentFriction: number | null;
    optimizedFriction: number | null;
    annualSavings: number | null;
    categoryImpacts: { [key: string]: { current: number; optimized: number; savings: number } };
    revenueUnmeasured: boolean;
  } {
    const frictionResult = this.calculateFrictionMultipliers(businessData);
    const { multipliers } = frictionResult;

    // Same guard as calculateFrictionMultipliers: a null multipliers set means
    // revenue was never measured — don't let it read as "$0 savings available".
    if (multipliers === null) {
      return {
        currentFriction: null,
        optimizedFriction: null,
        annualSavings: null,
        categoryImpacts: {},
        revenueUnmeasured: true
      };
    }

    let currentFriction = 0;
    let optimizedFriction = 0;
    const categoryImpacts: { [key: string]: { current: number; optimized: number; savings: number } } = {};
    
    // Define optimal zones (these would come from your readiness zone data)
    const optimalZones = {
      governance: { min: 70, max: 85 },
      culture: { min: 75, max: 90 },
      budget: { min: 65, max: 80 },
      technical_debt: { min: 60, max: 75 },
      third_party: { min: 70, max: 85 },
      people: { min: 75, max: 90 }
    };
    
    Object.keys(currentReadiness).forEach(category => {
      const currentValue = currentReadiness[category];
      const targetValue = targetReadiness[category];
      const optimal = optimalZones[category as keyof typeof optimalZones];
      const multiplier = multipliers[category as keyof typeof multipliers];
      
      if (optimal && multiplier) {
        // Calculate current friction
        let currentCategoryFriction = 0;
        if (currentValue < optimal.min) {
          currentCategoryFriction = multiplier.underOptimal;
        } else if (currentValue > optimal.max) {
          currentCategoryFriction = multiplier.overOptimal;
        }
        
        // Calculate optimized friction
        let optimizedCategoryFriction = 0;
        if (targetValue < optimal.min) {
          optimizedCategoryFriction = multiplier.underOptimal;
        } else if (targetValue > optimal.max) {
          optimizedCategoryFriction = multiplier.overOptimal;
        }
        
        currentFriction += currentCategoryFriction;
        optimizedFriction += optimizedCategoryFriction;
        
        categoryImpacts[category] = {
          current: currentCategoryFriction,
          optimized: optimizedCategoryFriction,
          savings: currentCategoryFriction - optimizedCategoryFriction
        };
      }
    });
    
    return {
      currentFriction,
      optimizedFriction,
      annualSavings: currentFriction - optimizedFriction,
      categoryImpacts,
      revenueUnmeasured: false
    };
  }
};

// Export types for use in other files
export type {
  BusinessIntelligenceResponse,    // Internal use only
  BusinessIntelligenceApiData,     // Internal use only
  BusinessIntelligenceData,        // Main interface - what API function returns
  FrictionMultipliers,
  FrictionCalculationResult,
  CategoryMultiplier,
  CurrencyScale,
  FormulaExplanation
};

// Export union type for flexibility in friction calculation functions
export type BusinessIntelligenceInput = BusinessIntelligenceResponse | BusinessIntelligenceApiData | BusinessIntelligenceData;